import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  Line
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Calendar,
  Award,
  Flame,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowLeft,
  Info,
  BookOpen,
  Layers,
  Hash,
  Trophy,
  Grid3X3,
  List
} from 'lucide-react';

// Paleta de colores para áreas
const AREA_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7', '#F43F5E', '#22C55E', '#0EA5E9'
];

// Extraer número de año de strings como "RM 2024"
const extractYear = (yearStr) => {
  if (!yearStr) return 0;
  const match = yearStr.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : 0;
};

// Componente de Card de Estadística
const StatCard = ({ title, value, subtitle, icon: Icon, trend, darkMode, accentColor = 'blue' }) => {
  const colorClasses = {
    blue: darkMode ? 'from-blue-500/20 to-cyan-500/20 border-blue-500/30' : 'from-blue-50 to-cyan-50 border-blue-200',
    green: darkMode ? 'from-emerald-500/20 to-green-500/20 border-emerald-500/30' : 'from-emerald-50 to-green-50 border-emerald-200',
    amber: darkMode ? 'from-amber-500/20 to-orange-500/20 border-amber-500/30' : 'from-amber-50 to-orange-50 border-amber-200',
    purple: darkMode ? 'from-purple-500/20 to-pink-500/20 border-purple-500/30' : 'from-purple-50 to-pink-50 border-purple-200',
    rose: darkMode ? 'from-rose-500/20 to-pink-500/20 border-rose-500/30' : 'from-rose-50 to-pink-50 border-rose-200',
  };

  const iconColors = {
    blue: darkMode ? 'text-blue-400' : 'text-blue-600',
    green: darkMode ? 'text-emerald-400' : 'text-emerald-600',
    amber: darkMode ? 'text-amber-400' : 'text-amber-600',
    purple: darkMode ? 'text-purple-400' : 'text-purple-600',
    rose: darkMode ? 'text-rose-400' : 'text-rose-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${colorClasses[accentColor]} p-5 ${
        darkMode ? 'bg-slate-900/60' : 'bg-white'
      } shadow-lg`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {title}
          </p>
          <p className={`mt-2 text-2xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-800'}`} title={typeof value === 'string' ? value : undefined}>
            {value}
          </p>
          {subtitle && (
            <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {subtitle}
            </p>
          )}
        </div>
        <div className={`rounded-xl p-3 ${darkMode ? 'bg-slate-800/50' : 'bg-white/80'} shadow-inner`}>
          <Icon className={`h-6 w-6 ${iconColors[accentColor]}`} />
        </div>
      </div>
      {trend !== undefined && trend !== null && (
        <div className={`mt-3 flex items-center gap-1 text-sm ${
          trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-rose-500' : darkMode ? 'text-slate-400' : 'text-slate-500'
        }`}>
          {trend > 0 ? <TrendingUp className="h-4 w-4" /> : trend < 0 ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          <span>{trend > 0 ? '+' : ''}{trend.toFixed(1)}% vs año anterior</span>
        </div>
      )}
    </motion.div>
  );
};

// Componente Heatmap Áreas x Temas
const AreasTopicsHeatmap = ({ data, areas, darkMode, onCellClick }) => {
  const maxValue = useMemo(() => {
    let max = 0;
    data.forEach(row => {
      areas.forEach(area => {
        if (row[area] > max) max = row[area];
      });
    });
    return max || 1;
  }, [data, areas]);

  const getHeatColor = (value) => {
    if (value === 0) return darkMode ? 'bg-slate-800/50' : 'bg-slate-100';
    const intensity = value / maxValue;
    if (intensity < 0.25) return darkMode ? 'bg-blue-900/40' : 'bg-blue-100';
    if (intensity < 0.5) return darkMode ? 'bg-blue-700/50' : 'bg-blue-300';
    if (intensity < 0.75) return darkMode ? 'bg-blue-500/60' : 'bg-blue-500';
    return darkMode ? 'bg-blue-400' : 'bg-blue-700';
  };

  const getTextColor = (value) => {
    if (value === 0) return darkMode ? 'text-slate-600' : 'text-slate-400';
    const intensity = value / maxValue;
    if (intensity < 0.5) return darkMode ? 'text-blue-200' : 'text-blue-800';
    return 'text-white';
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Header con áreas */}
        <div className="flex">
          <div className={`w-36 flex-shrink-0 p-2 text-xs font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Tema
          </div>
          {areas.map((area, idx) => (
            <div
              key={area}
              className={`flex-1 p-2 text-center text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}
              style={{ minWidth: '60px' }}
              title={area}
            >
              <span className="truncate block">{area.length > 10 ? area.substring(0, 10) + '...' : area}</span>
            </div>
          ))}
          <div className={`w-14 flex-shrink-0 p-2 text-center text-xs font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
            Total
          </div>
        </div>

        {/* Filas de temas */}
        {data.slice(0, 15).map((row, idx) => (
          <motion.div
            key={row.topic}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.02 }}
            className={`flex items-center border-t ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}
          >
            <div
              className={`w-36 flex-shrink-0 p-2 text-xs font-medium truncate ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}
              title={row.topic}
            >
              {row.topic}
            </div>
            {areas.map(area => (
              <div
                key={`${row.topic}-${area}`}
                className="flex-1 p-0.5"
                style={{ minWidth: '60px' }}
              >
                <div
                  className={`mx-auto h-7 w-full max-w-[50px] rounded flex items-center justify-center text-xs font-semibold cursor-pointer hover:scale-110 transition-transform ${getHeatColor(row[area] || 0)} ${getTextColor(row[area] || 0)}`}
                  title={`${row.topic} - ${area}: ${row[area] || 0} preguntas`}
                  onClick={() => onCellClick && onCellClick(row.topic, area)}
                >
                  {row[area] || '-'}
                </div>
              </div>
            ))}
            <div className={`w-14 flex-shrink-0 p-2 text-center text-xs font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              {row.total}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Leyenda */}
      <div className={`mt-4 flex items-center justify-center gap-4 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        <span>Intensidad:</span>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`} />
          <span>0</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`} />
          <span>Bajo</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-500/60' : 'bg-blue-500'}`} />
          <span>Medio</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`h-4 w-4 rounded ${darkMode ? 'bg-blue-400' : 'bg-blue-700'}`} />
          <span>Alto</span>
        </div>
      </div>
    </div>
  );
};

// Componente Principal
const YearAnalysisDashboard = ({ questions = [], darkMode = false, onBack, onGenerateExam }) => {
  // Estados
  const [selectedYears, setSelectedYears] = useState([]);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'areas', 'topics', 'heatmap', 'compare'
  const [topicsLimit, setTopicsLimit] = useState(20); // 20 o 50

  // Obtener lista de años únicos ordenados
  const years = useMemo(() => {
    const yearSet = new Set();
    questions.forEach(q => {
      if (q.year) yearSet.add(q.year);
    });
    return Array.from(yearSet).sort((a, b) => extractYear(b) - extractYear(a)); // Más reciente primero
  }, [questions]);

  // Auto-seleccionar el año más reciente
  React.useEffect(() => {
    if (selectedYears.length === 0 && years.length > 0) {
      setSelectedYears([years[0]]);
    }
  }, [years, selectedYears.length]);

  // Toggle selección de año
  const toggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        // No permitir deseleccionar si es el único
        if (prev.length === 1) return prev;
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year].sort((a, b) => extractYear(b) - extractYear(a));
      }
    });
  };

  // Seleccionar todos / ninguno
  const selectAllYears = () => setSelectedYears([...years]);
  const selectOnlyYear = (year) => setSelectedYears([year]);

  // Filtrar preguntas por años seleccionados
  const yearQuestions = useMemo(() => {
    return questions.filter(q => selectedYears.includes(q.year));
  }, [questions, selectedYears]);

  // Años no seleccionados (para comparación)
  const unselectedYears = useMemo(() => {
    return years.filter(y => !selectedYears.includes(y));
  }, [years, selectedYears]);

  // Preguntas de años no seleccionados (para comparación)
  const unselectedYearQuestions = useMemo(() => {
    return questions.filter(q => unselectedYears.includes(q.year));
  }, [questions, unselectedYears]);

  // Datos por año individual (para gráficos comparativos)
  const dataByYear = useMemo(() => {
    const result = {};
    selectedYears.forEach(year => {
      result[year] = questions.filter(q => q.year === year);
    });
    return result;
  }, [questions, selectedYears]);

  // Obtener áreas únicas del año
  const areasData = useMemo(() => {
    const areaCounts = {};
    yearQuestions.forEach(q => {
      if (q.area) {
        areaCounts[q.area] = (areaCounts[q.area] || 0) + 1;
      }
    });
    return Object.entries(areaCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([area, count], index) => ({
        area,
        count,
        percentage: Math.round((count / yearQuestions.length) * 100),
        color: AREA_COLORS[index % AREA_COLORS.length]
      }));
  }, [yearQuestions]);

  // Obtener temas únicos del año (top 20 o 50)
  const topicsData = useMemo(() => {
    const topicCounts = {};
    yearQuestions.forEach(q => {
      if (q.topic) {
        if (!topicCounts[q.topic]) {
          topicCounts[q.topic] = { count: 0, area: q.area };
        }
        topicCounts[q.topic].count += 1;
      }
    });
    return Object.entries(topicCounts)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, topicsLimit)
      .map(([topic, data], index) => ({
        topic,
        count: data.count,
        area: data.area,
        color: AREA_COLORS[index % AREA_COLORS.length]
      }));
  }, [yearQuestions, topicsLimit]);

  // Datos para heatmap Áreas x Temas
  const heatmapData = useMemo(() => {
    const topTopics = topicsData.slice(0, 15);
    const topAreas = areasData.slice(0, 8).map(a => a.area);

    return topTopics.map(({ topic }) => {
      const row = { topic, total: 0 };
      topAreas.forEach(area => {
        const count = yearQuestions.filter(q => q.topic === topic && q.area === area).length;
        row[area] = count;
        row.total += count;
      });
      return row;
    });
  }, [topicsData, areasData, yearQuestions]);

  // Datos comparativos por año para áreas
  const yearAreaComparison = useMemo(() => {
    const allAreas = [...new Set(yearQuestions.map(q => q.area).filter(Boolean))];

    return allAreas.map((area, index) => {
      const row = { area, total: 0, color: AREA_COLORS[index % AREA_COLORS.length] };
      selectedYears.forEach(year => {
        const count = dataByYear[year]?.filter(q => q.area === area).length || 0;
        row[year] = count;
        row.total += count;
      });
      return row;
    }).sort((a, b) => b.total - a.total);
  }, [yearQuestions, selectedYears, dataByYear]);

  // Datos comparativos por año para temas
  const yearTopicComparison = useMemo(() => {
    const topicCounts = {};
    yearQuestions.forEach(q => {
      if (q.topic) {
        if (!topicCounts[q.topic]) {
          topicCounts[q.topic] = { total: 0, area: q.area };
        }
        topicCounts[q.topic].total += 1;
      }
    });

    return Object.entries(topicCounts)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, topicsLimit)
      .map(([topic, data], index) => {
        const row = { topic, total: data.total, area: data.area, color: AREA_COLORS[index % AREA_COLORS.length] };
        selectedYears.forEach(year => {
          row[year] = dataByYear[year]?.filter(q => q.topic === topic).length || 0;
        });
        return row;
      });
  }, [yearQuestions, selectedYears, dataByYear, topicsLimit]);

  // Comparativa con promedio histórico
  const historicalComparison = useMemo(() => {
    const avgByArea = {};

    questions.forEach(q => {
      if (q.area && q.year) {
        if (!avgByArea[q.area]) {
          avgByArea[q.area] = {};
        }
        avgByArea[q.area][q.year] = (avgByArea[q.area][q.year] || 0) + 1;
      }
    });

    return areasData.map(({ area, count, color }) => {
      const yearCounts = Object.values(avgByArea[area] || {});
      const avg = yearCounts.length > 0
        ? yearCounts.reduce((a, b) => a + b, 0) / yearCounts.length
        : 0;
      const avgSelected = selectedYears.length > 0 ? count / selectedYears.length : count;
      const diff = avgSelected - avg;
      const diffPercent = avg > 0 ? ((avgSelected - avg) / avg) * 100 : 0;

      return {
        area,
        current: count,
        avgPerYear: Math.round(avgSelected * 10) / 10,
        average: Math.round(avg * 10) / 10,
        diff: Math.round(diff * 10) / 10,
        diffPercent: Math.round(diffPercent),
        color
      };
    });
  }, [questions, areasData, selectedYears.length]);

  // Totales por año para gráfico de tendencia
  const totalsByYear = useMemo(() => {
    return selectedYears.map(year => ({
      year: year.replace('RM ', ''),
      fullYear: year,
      total: dataByYear[year]?.length || 0
    })).sort((a, b) => extractYear(a.fullYear) - extractYear(b.fullYear));
  }, [selectedYears, dataByYear]);

  // Estadísticas generales
  const stats = useMemo(() => {
    const totalQuestions = yearQuestions.length;
    const totalAreas = areasData.length;
    const topArea = areasData[0] || { area: 'N/A', count: 0 };
    const topTopic = topicsData[0] || { topic: 'N/A', count: 0 };
    const avgPerYear = selectedYears.length > 0 ? Math.round(totalQuestions / selectedYears.length) : totalQuestions;

    // Conteo único de temas
    const uniqueTopics = new Set(yearQuestions.map(q => q.topic).filter(Boolean)).size;

    return {
      totalQuestions,
      totalAreas,
      topArea,
      topTopic,
      avgPerYear,
      uniqueTopics,
      yearsCount: selectedYears.length
    };
  }, [yearQuestions, areasData, topicsData, selectedYears.length]);

  // Datos para pie chart de áreas
  const pieData = useMemo(() => {
    return areasData.slice(0, 8).map(({ area, count, color }) => ({
      name: area,
      value: count,
      fill: color
    }));
  }, [areasData]);

  // Datos para radar chart
  const radarData = useMemo(() => {
    return areasData.slice(0, 8).map(({ area, count, percentage }) => ({
      area: area.length > 12 ? area.substring(0, 12) + '...' : area,
      fullName: area,
      preguntas: count,
      porcentaje: percentage
    }));
  }, [areasData]);

  // Texto descriptivo de años seleccionados
  const selectedYearsLabel = useMemo(() => {
    if (selectedYears.length === 0) return 'Ninguno';
    if (selectedYears.length === 1) return selectedYears[0];
    if (selectedYears.length === years.length) return 'Todos los años';
    if (selectedYears.length <= 3) return selectedYears.join(', ');
    return `${selectedYears.length} años`;
  }, [selectedYears, years.length]);

  // Renderizar vista según modo seleccionado
  const renderContent = () => {
    switch (viewMode) {
      case 'areas':
        return (
          <div className="space-y-6">
            {/* Distribución de Áreas - Barras Horizontales */}
            <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Distribución por Área {selectedYears.length > 1 ? `(${selectedYears.length} años)` : ''}
              </h3>
              {selectedYears.length > 1 ? (
                /* Vista comparativa multi-año */
                <div style={{ height: `${Math.max(450, yearAreaComparison.length * 38)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={yearAreaComparison}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis type="number" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="area"
                        tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                        width={130}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      {selectedYears.map((year, idx) => (
                        <Bar
                          key={year}
                          dataKey={year}
                          name={year.replace('RM ', '')}
                          fill={AREA_COLORS[idx % AREA_COLORS.length]}
                          radius={[0, 4, 4, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                /* Vista de un solo año */
                <div style={{ height: `${Math.max(450, areasData.length * 38)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={areasData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      barSize={24}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis type="number" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="area"
                        tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                        width={130}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [`${value} preguntas (${areasData.find(a => a.count === value)?.percentage || 0}%)`, 'Total']}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {areasData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Radar de Áreas */}
            <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Perfil del Examen
              </h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <PolarAngleAxis
                      dataKey="area"
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                    />
                    <PolarRadiusAxis
                      angle={30}
                      domain={[0, 'auto']}
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                    />
                    <Radar
                      name="Preguntas"
                      dataKey="preguntas"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.5}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'topics':
        return (
          <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Top {topicsLimit} Temas {selectedYears.length > 1 ? `(${selectedYears.length} años)` : ''}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTopicsLimit(20)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    topicsLimit === 20
                      ? 'bg-blue-500 text-white'
                      : darkMode
                        ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Top 20
                </button>
                <button
                  onClick={() => setTopicsLimit(50)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    topicsLimit === 50
                      ? 'bg-blue-500 text-white'
                      : darkMode
                        ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Top 50
                </button>
              </div>
            </div>
            {selectedYears.length > 1 ? (
              /* Vista comparativa multi-año */
              <div style={{ height: `${Math.max(450, yearTopicComparison.length * 38)}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={yearTopicComparison}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis type="number" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="topic"
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                      width={130}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [`${value} preguntas`, name]}
                    />
                    <Legend />
                    {selectedYears.map((year, idx) => (
                      <Bar
                        key={year}
                        dataKey={year}
                        name={year.replace('RM ', '')}
                        fill={AREA_COLORS[idx % AREA_COLORS.length]}
                        radius={[0, 4, 4, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              /* Vista de un solo año */
              <div style={{ height: `${Math.max(450, topicsData.length * 38)}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topicsData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    barSize={24}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis type="number" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="topic"
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                      width={130}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [`${value} preguntas`, props.payload.area]}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {topicsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );

      case 'heatmap':
        return (
          <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Heatmap: Temas × Áreas
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Distribución de los top 15 temas en las principales áreas
            </p>
            <AreasTopicsHeatmap
              data={heatmapData}
              areas={areasData.slice(0, 8).map(a => a.area)}
              darkMode={darkMode}
            />
          </div>
        );

      case 'compare':
        return (
          <div className="space-y-6">
            {/* Tendencia de preguntas por año */}
            {selectedYears.length > 1 && (
              <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Total de Preguntas por Año
                </h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={totalsByYear}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="year" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value} preguntas`, 'Total']}
                      />
                      <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                        {totalsByYear.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={AREA_COLORS[index % AREA_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Comparativa de Áreas entre años seleccionados */}
            {selectedYears.length > 1 && (
              <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Comparativa de Áreas ({selectedYears.length} años)
                </h3>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={yearAreaComparison.slice(0, 10)}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis
                        dataKey="area"
                        tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      {selectedYears.map((year, idx) => (
                        <Bar
                          key={year}
                          dataKey={year}
                          name={year.replace('RM ', '')}
                          fill={AREA_COLORS[idx % AREA_COLORS.length]}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Comparativa vs Promedio Histórico */}
            <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {selectedYears.length === 1 ? selectedYears[0] : 'Selección'} vs Promedio Histórico
              </h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={historicalComparison.slice(0, 10)}
                    margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis type="number" tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="area"
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                      width={95}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'avgPerYear') return [`${value} preguntas/año`, 'Promedio selección'];
                        if (name === 'average') return [`${value} preguntas/año`, 'Promedio histórico'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="avgPerYear" name="Promedio selección" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    <Line type="monotone" dataKey="average" name="Promedio histórico" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Resumen de diferencias vs promedio histórico */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'} border ${darkMode ? 'border-emerald-500/30' : 'border-emerald-200'}`}>
                  <h5 className={`flex items-center gap-2 text-sm font-semibold mb-2 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    <TrendingUp className="h-4 w-4" />
                    Sobre el promedio
                  </h5>
                  <ul className={`space-y-1 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                    {historicalComparison.filter(c => c.diffPercent > 10).slice(0, 3).map(c => (
                      <li key={c.area} className="flex justify-between">
                        <span className="truncate mr-2">{c.area}</span>
                        <span className="text-emerald-500 font-semibold">+{c.diffPercent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/30' : 'bg-gray-100'}`}>
                  <h5 className={`flex items-center gap-2 text-sm font-semibold mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    <Minus className="h-4 w-4" />
                    En el promedio
                  </h5>
                  <ul className={`space-y-1 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                    {historicalComparison.filter(c => c.diffPercent >= -10 && c.diffPercent <= 10).slice(0, 3).map(c => (
                      <li key={c.area} className="flex justify-between">
                        <span className="truncate mr-2">{c.area}</span>
                        <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>{c.diffPercent > 0 ? '+' : ''}{c.diffPercent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`p-4 rounded-xl ${darkMode ? 'bg-rose-900/20' : 'bg-rose-50'} border ${darkMode ? 'border-rose-500/30' : 'border-rose-200'}`}>
                  <h5 className={`flex items-center gap-2 text-sm font-semibold mb-2 ${darkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                    <TrendingDown className="h-4 w-4" />
                    Bajo el promedio
                  </h5>
                  <ul className={`space-y-1 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                    {historicalComparison.filter(c => c.diffPercent < -10).slice(0, 3).map(c => (
                      <li key={c.area} className="flex justify-between">
                        <span className="truncate mr-2">{c.area}</span>
                        <span className="text-rose-500 font-semibold">{c.diffPercent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'overview':
      default:
        return (
          <div className="space-y-6">
            {/* Distribución de Áreas - Pie + Lista */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Distribución por Área
                </h3>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [`${value} preguntas`, name]}
                      />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Ranking de Áreas
                </h3>
                <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2">
                  {areasData.map((item, index) => (
                    <div key={item.area} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-amber-400 text-amber-900' :
                        index === 1 ? 'bg-slate-300 text-slate-700' :
                        index === 2 ? 'bg-amber-600 text-white' :
                        darkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium truncate ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                            {item.area}
                          </span>
                          <span className={`text-sm font-semibold ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {item.count}
                          </span>
                        </div>
                        <div className={`h-2 rounded-full mt-1 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${item.percentage}%`,
                              backgroundColor: item.color
                            }}
                          />
                        </div>
                      </div>
                      <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {item.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top 10 Temas */}
            <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 shadow-lg`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Top 10 Temas del Año
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topicsData.slice(0, 10)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis
                      dataKey="topic"
                      tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [`${value} preguntas`, props.payload.area]}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {topicsData.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Insights del Año */}
            <div className={`rounded-2xl border ${
              darkMode
                ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/30'
                : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
            } p-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Insights {selectedYears.length === 1 ? `de ${selectedYears[0]}` : `(${selectedYears.length} años)`}
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                  <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                    <Trophy className="h-4 w-4" />
                    Áreas Dominantes
                  </h4>
                  <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {areasData.slice(0, 3).map((item, idx) => (
                      <li key={item.area} className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-amber-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          'bg-amber-600 text-white'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="truncate">{item.area}</span>
                        <span className="ml-auto font-semibold">{item.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                  <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    <Flame className="h-4 w-4" />
                    Temas Más Frecuentes
                  </h4>
                  <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {topicsData.slice(0, 3).map(item => (
                      <li key={item.topic} className="flex items-center justify-between">
                        <span className="truncate mr-2">{item.topic}</span>
                        <span className="font-semibold">{item.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-800/50' : 'bg-white'}`}>
                  <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                    <Info className="h-4 w-4" />
                    Resumen
                  </h4>
                  <ul className={`space-y-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    <li className="flex justify-between">
                      <span>Total preguntas:</span>
                      <span className="font-semibold">{stats.totalQuestions}</span>
                    </li>
                    {selectedYears.length > 1 && (
                      <li className="flex justify-between">
                        <span>Promedio/año:</span>
                        <span className="font-semibold">{stats.avgPerYear}</span>
                      </li>
                    )}
                    <li className="flex justify-between">
                      <span>Áreas evaluadas:</span>
                      <span className="font-semibold">{stats.totalAreas}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Temas únicos:</span>
                      <span className="font-semibold">{stats.uniqueTopics}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className={`p-2 rounded-xl transition-colors ${
                  darkMode
                    ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                    : 'hover:bg-gray-200 text-gray-600 hover:text-gray-800'
                }`}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Análisis por Año
              </h1>
              <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Analiza la distribución de preguntas en un año específico
              </p>
            </div>
          </div>

          {/* Indicador de años seleccionados */}
          <div className="flex items-center gap-2">
            <Calendar className={`h-5 w-5 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
            <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              {selectedYearsLabel}
            </span>
          </div>
        </div>

        {/* Selector múltiple de años */}
        <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-4 mb-6 shadow-lg`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-sm font-semibold ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Años:
            </span>

            {/* Botones de selección rápida */}
            <button
              onClick={selectAllYears}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                selectedYears.length === years.length
                  ? 'bg-blue-500 text-white'
                  : darkMode
                    ? 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
              }`}
            >
              Todos
            </button>

            <div className="h-5 w-px bg-slate-600/30" />

            {/* Chips de años */}
            <div className="flex flex-wrap gap-2">
              {years.map(year => {
                const isSelected = selectedYears.includes(year);
                const yearNum = extractYear(year);
                return (
                  <button
                    key={year}
                    onClick={() => toggleYear(year)}
                    onDoubleClick={() => selectOnlyYear(year)}
                    title="Click para seleccionar/deseleccionar, doble click para seleccionar solo este año"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                        : darkMode
                          ? 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                    }`}
                  >
                    {yearNum}
                  </button>
                );
              })}
            </div>

            {selectedYears.length > 1 && (
              <>
                <div className="h-5 w-px bg-slate-600/30" />
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  {selectedYears.length} seleccionados
                </span>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard
            title="Total Preguntas"
            value={stats.totalQuestions}
            subtitle={selectedYears.length > 1 ? `~${stats.avgPerYear}/año` : undefined}
            icon={BookOpen}
            darkMode={darkMode}
            accentColor="blue"
          />
          <StatCard
            title="Áreas Evaluadas"
            value={stats.totalAreas}
            subtitle={selectedYears.length > 1 ? `en ${stats.yearsCount} años` : undefined}
            icon={Layers}
            darkMode={darkMode}
            accentColor="green"
          />
          <StatCard
            title="Área Líder"
            value={stats.topArea.area}
            subtitle={`${stats.topArea.count} preguntas`}
            icon={Award}
            darkMode={darkMode}
            accentColor="amber"
          />
          <StatCard
            title="Tema Top"
            value={stats.topTopic.topic}
            subtitle={`${stats.topTopic.count} preguntas`}
            icon={Flame}
            darkMode={darkMode}
            accentColor="rose"
          />
          <StatCard
            title="Temas Únicos"
            value={stats.uniqueTopics}
            icon={Hash}
            darkMode={darkMode}
            accentColor="purple"
          />
        </div>

        {/* Tabs de visualización */}
        <div className={`rounded-2xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} p-6 mb-6 shadow-lg`}>
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'overview', label: 'Resumen', icon: Grid3X3 },
              { id: 'areas', label: 'Áreas', icon: PieChartIcon },
              { id: 'topics', label: 'Temas', icon: List },
              { id: 'heatmap', label: 'Heatmap', icon: BarChart3 },
              { id: 'compare', label: 'Comparar', icon: Activity },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setViewMode(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === id
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                    : darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default YearAnalysisDashboard;
